// This file is part of Moodle - http://moodle.org/
// Moodle is free software, distributed under the GNU GPL v3 or later.

/**
 * Explicit URL matching and input-safe restriction decisions.
 *
 * @module local_disablerightclick/policy
 * @copyright 2020 Yogesh Shirsath <yogshirsath@hotmail.com>
 * @license http://www.gnu.org/copyleft/gpl.html GNU GPL v3 or later
 */

/**
 * Normalize supported settings; malformed or missing values enable nothing.
 * @param {Object} settings Server configuration.
 * @returns {Object}
 */
export const normaliseSettings = settings => {
    const input = settings && typeof settings === 'object' && !Array.isArray(settings) ? settings : {};
    const output = {};
    for (const key of ['disablerightclick', 'disablecutcopypaste', 'disabledevelopertools']) {
        output[key] = input[key] === true || input[key] === 1 || input[key] === '1';
    }
    for (const key of ['allowall', 'allowrightclick', 'allowcutcopypaste', 'allowdevelopertools']) {
        output[key] = typeof input[key] === 'string' ? input[key].trim() : '';
    }
    return output;
};

/**
 * Recognise the declared response shape before enabling any restriction.
 * @param {*} response Server response.
 * @returns {Boolean} Whether the policy has a valid envelope.
 */
export const validPolicy = response => Boolean(response && typeof response === 'object' &&
    typeof response.enabled === 'boolean' && response.settings && typeof response.settings === 'object' &&
    !Array.isArray(response.settings));

/**
 * Match an exact path, an explicit trailing-slash subtree, or an absolute HTTP(S) URL.
 * Specified query keys must have identical values; additional keys are allowed.
 * @param {String} address Actual document URL.
 * @param {String} rules Newline-separated exceptions.
 * @returns {Boolean}
 */
export const matchesUrl = (address, rules) => {
    let current;
    try {
        current = new URL(address);
    } catch (error) {
        return false;
    }
    if (!['http:', 'https:'].includes(current.protocol) || typeof rules !== 'string') {
        return false;
    }
    return rules.split(/\r?\n/).some(line => {
        const rule = line.trim();
        if (!rule || [...rule].some(character => character.charCodeAt(0) <= 32) ||
                rule.includes('#') || rule.includes('\\') || rule.includes('*') ||
                (!/^https?:\/\//i.test(rule) && !/^\/(?!\/)/.test(rule))) {
            return false;
        }
        let allowed;
        try {
            allowed = new URL(rule, current.origin);
        } catch (error) {
            return false;
        }
        if (allowed.origin !== current.origin || allowed.username || allowed.password) {
            return false;
        }
        const subtree = allowed.pathname !== '/' && allowed.pathname.endsWith('/');
        if (subtree ? !current.pathname.startsWith(allowed.pathname) : current.pathname !== allowed.pathname) {
            return false;
        }
        return [...new Set(allowed.searchParams.keys())].every(key => {
            const expected = allowed.searchParams.getAll(key).sort();
            const actual = current.searchParams.getAll(key).sort();
            return expected.length === actual.length && expected.every((value, index) => value === actual[index]);
        });
    });
};

/**
 * Decide each restriction independently for the current document.
 * @param {String} address Actual document URL.
 * @param {Object} settings Normalized settings.
 * @returns {Object}
 */
export const restrictionsFor = (address, settings) => {
    const all = matchesUrl(address, settings.allowall);
    return {
        contextmenu: !all && settings.disablerightclick && !matchesUrl(address, settings.allowrightclick),
        clipboard: !all && settings.disablecutcopypaste && !matchesUrl(address, settings.allowcutcopypaste),
        shortcuts: !all && settings.disabledevelopertools && !matchesUrl(address, settings.allowdevelopertools),
    };
};

/**
 * Keep native interactions in form controls and editor surfaces, including iframe editors.
 * @param {Event} event User interaction.
 * @param {Document} doc Event document.
 * @returns {Boolean}
 */
export const isEditing = (event, doc) => {
    if (String(doc.designMode).toLowerCase() === 'on') {
        return true;
    }
    const editable = element => element && element.nodeType === 1 && (element.isContentEditable ||
        element.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"]), ' +
            '[role="textbox"], [role="combobox"], .tox, .editor_atto, .CodeMirror, .cm-editor'));
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    return path.some(editable) ||
        (['copy', 'cut', 'paste'].includes(event.type) && Boolean(editable(doc.activeElement)));
};

/**
 * Identify conventional shortcuts without detecting whether developer tools are open.
 * @param {KeyboardEvent} event Keyboard interaction.
 * @returns {Boolean}
 */
export const isDeveloperShortcut = event => {
    const key = String(event.key || '').toLowerCase();
    return key === 'f12' ||
        (event.ctrlKey && !event.altKey && !event.metaKey &&
            ((event.shiftKey && ['i', 'j', 'c', 'k'].includes(key)) || (!event.shiftKey && key === 'u'))) ||
        (event.metaKey && event.altKey && !event.ctrlKey && ['i', 'j', 'c', 'u'].includes(key));
};

/**
 * Identify clipboard shortcuts without also capturing editor commands.
 * @param {KeyboardEvent} event Keyboard interaction.
 * @returns {Boolean} Whether the event is a plain copy, cut or paste shortcut.
 */
export const isClipboardShortcut = event => Boolean((event.ctrlKey || event.metaKey) &&
    !event.shiftKey && !event.altKey && ['c', 'x', 'v'].includes(String(event.key || '').toLowerCase()));

/**
 * Ignore unrelated keys before querying the DOM or parsing exception URLs.
 * @param {Event} event Interaction.
 * @returns {String|null} Feature that could restrict this interaction.
 */
export const interactionFeature = event => {
    if (event.type === 'contextmenu') {
        return 'contextmenu';
    }
    if (['copy', 'cut', 'paste'].includes(event.type)) {
        return 'clipboard';
    }
    if (event.type === 'keydown') {
        if (isDeveloperShortcut(event)) {
            return 'shortcuts';
        }
        if (isClipboardShortcut(event)) {
            return 'clipboard';
        }
    }
    return null;
};
